/**
 * 振り返り（日報）と、LINE へ貼る報告文。DAILY-WORKFLOW-4。
 *
 * 権限の唯一の根拠は `docs/05` §3.1「日報（営業日の振り返りコメント）」= manager 以上。
 * ★見せる／見せないは**表示の都合**で、担保はサーバの 403（絶対ルール1）。
 *
 * ★**送信しない。** コピーしただけで「報告済み」とは書かない。
 * ★報告文は**登録済みの業務データ**から作る（OCR の読取候補は使わない）。
 */
import { buildDailyReportText, manText, reportFigureReasons, type ReportFigures } from "../domain/dailyReport.js";
import { ApiError } from "./api.js";

export interface DailyReportApi {
  save(comment: string): Promise<{ comment: string }>;
  /**
   * ④ 翌日の見込みを保存し、報告文の数値を取り直して返す。入力の `null` は未入力（消す）。
   * ★戻り値が `null`＝保存は成功したが、取り直しに失敗した（保存失敗とは区別する）
   */
  saveForecast(input: {
    purchaseEstimate: number | null;
    salesEstimate: number | null;
    /** ③ 明日の予約・シフト人数（名・組）。空欄は null */
    reservationGroups: number | null;
    reservationGuests: number | null;
    courseGuests: number | null;
    shiftStaff: number | null;
  }): Promise<ReportFigures | null>;
  /**
   * ③ を予約サイトから読む（docs/21）。★読むだけ。保存はしない。失敗は reject。
   * 無ければ（古い呼び出し元）ボタンを出さない
   */
  loadReservation?(): Promise<{ reservationGroups: number; reservationGuests: number; courseGuests: number }>;
}

/** 画面が閉じるまで持つ編集状態。★保存していない本文・見込みを黙って捨てない */
let dirtyComment: string | null = null;
let dirtyForecast = false;
export const hasUnsavedDailyReport = (): boolean => dirtyComment !== null || dirtyForecast;
/** タブ・日付を移る前に呼ぶ。破棄を選んだら編集中の印を落とす */
export function discardDailyReportDraft(): void {
  dirtyComment = null;
  dirtyForecast = false;
}

/**
 * 見込みの入力欄 → API へ送る値。★**空欄は 0 ではなく `null`（未入力）。**
 * `Number("")` も `Number("  ")` も 0 で、既存の見込みを 0 円で上書きしてしまう。
 * 全角数字・桁区切りは受ける。負数・小数・文字は `undefined`（送らない）。
 */
export function forecastAmountToSend(text: string): number | null | undefined {
  const t = text.normalize("NFKC").replace(/[,\s]/g, "");
  if (t === "") return null;
  return /^\d+$/.test(t) && Number.isSafeInteger(Number(t)) ? Number(t) : undefined;
}

const el = <K extends keyof HTMLElementTagNameMap>(tag: K, text?: string): HTMLElementTagNameMap[K] => {
  const node = document.createElement(tag);
  if (text !== undefined) node.textContent = text;
  return node;
};

export function dailyReportView(input: {
  businessDate: string;
  /** 保存済みの本文。無ければ null */
  saved: string | null;
  /** サーバが計算した報告文の数値（docs/02 §2.4） */
  figures: ReportFigures;
  /** 報告文の末尾に付ける定型文（設定タブで保存）。未設定は null */
  footer?: string | null;
  api: DailyReportApi;
  /** 差し替え可能にしてあるのは、クリップボードを持たない環境を検査で通すため */
  clipboard?: { writeText(text: string): Promise<void> } | undefined;
}): HTMLElement {
  dirtyComment = null;
  dirtyForecast = false;
  let figures = input.figures;
  const section = el("section");
  section.className = "daily-report";
  section.append(el("h2", "振り返り・報告"));

  const label = el("label", "今日の振り返り");
  const area = el("textarea");
  area.rows = 4;
  area.maxLength = 2000;
  area.value = input.saved ?? "";
  label.append(area);
  section.append(label);

  const save = el("button", "振り返りを保存");
  save.type = "button";
  const status = el("p");
  status.setAttribute("role", "status");
  section.append(save, status);

  // ---- ③ 明日の予約・シフト人数（数字で入れる。空欄＝未入力。そのまま報告文に写る） ----
  const tomorrowBox = el("fieldset");
  tomorrowBox.append(el("legend", "明日の予約・シフト人数"));
  const countField = (text: string, saved: number | null): HTMLInputElement => {
    const label = el("label", text);
    const field = el("input");
    field.type = "text";
    field.inputMode = "numeric";
    field.value = saved === null ? "" : String(saved);
    label.append(field);
    tomorrowBox.append(label);
    return field;
  };
  const fc = input.figures.forecast;
  const groupsField = countField("明日の予約（組）", fc.reservationGroups);
  const guestsField = countField("明日の予約（名）", fc.reservationGuests);
  const courseField = countField("明日のコース（名）", fc.courseGuests);
  const shiftField = countField("明日のシフト人数（名）", fc.shiftStaff);
  const countFields = [groupsField, guestsField, courseField, shiftField];
  const loadReservation = input.api.loadReservation;
  const loadButton = el("button", "予約サイトから読み込む");
  loadButton.type = "button";
  if (loadReservation !== undefined) tomorrowBox.append(loadButton);
  const savedCounts = (f: ReportFigures["forecast"]): string[] =>
    [f.reservationGroups, f.reservationGuests, f.courseGuests, f.shiftStaff].map((v) => (v === null ? "" : String(v)));
  let savedCountTexts = savedCounts(fc);
  section.append(tomorrowBox);

  // ---- ④ 翌日の見込み（円で入れる。空欄＝未入力）。計算は報告文の側（サーバ）で済む ----
  const forecastBox = el("fieldset");
  forecastBox.append(el("legend", "翌日の見込み（円）"));
  const amountField = (text: string, saved: number | null): HTMLInputElement => {
    const label = el("label", text);
    const field = el("input");
    field.type = "text";
    field.inputMode = "numeric";
    field.value = saved === null ? "" : String(saved);
    // ★万円換算を打った場面で出す。万円のつもりで「20」と打つと 20円で保存される事故を、保存前に気づかせる（保存はブロックしない）
    const hint = el("span");
    hint.className = "forecast-man muted";
    const showHint = (): void => {
      const yen = forecastAmountToSend(field.value);
      hint.textContent = typeof yen === "number" ? `＝${manText(yen)}` : "";
    };
    field.addEventListener("input", showHint);
    showHint();
    label.append(field, hint);
    forecastBox.append(label);
    return field;
  };
  const savedText = (v: number | null): string => (v === null ? "" : String(v));
  let savedPurchase = savedText(input.figures.forecast.purchaseEstimate.value);
  let savedSales = savedText(input.figures.forecast.salesEstimate.value);
  const purchaseField = amountField("翌日の仕入見込み（税込）", input.figures.forecast.purchaseEstimate.value);
  const salesField = amountField("明日の売上見込み（税抜）", input.figures.forecast.salesEstimate.value);
  const saveForecast = el("button", "見込みを保存");
  saveForecast.type = "button";
  const forecastStatus = el("p");
  forecastStatus.setAttribute("role", "status");
  // ★保存ボタンは③と④の両方の下に1つ（同じ PUT で送る）
  section.append(forecastBox, saveForecast, forecastStatus);
  const markForecast = (): void => {
    dirtyForecast =
      purchaseField.value !== savedPurchase ||
      salesField.value !== savedSales ||
      countFields.some((f, i) => f.value !== savedCountTexts[i]);
    forecastStatus.textContent = dirtyForecast ? "編集中（未保存）" : "";
  };
  purchaseField.addEventListener("input", markForecast);
  salesField.addEventListener("input", markForecast);
  for (const f of countFields) f.addEventListener("input", markForecast);

  // ★読み込みは3欄を書き換えて「編集中（未保存）」にするだけ。保存（PUT）は店長が押す。シフト人数は触らない
  loadButton.addEventListener("click", () => {
    if (loadReservation === undefined) return;
    // ★保存と同時に走らせない（保存完了時に欄を保存値で上書きするので、読み込んだ値が消える）
    loadButton.disabled = true;
    saveForecast.disabled = true;
    forecastStatus.textContent = "読み込み中…";
    void loadReservation()
      .then((r) => {
        groupsField.value = String(r.reservationGroups);
        guestsField.value = String(r.reservationGuests);
        courseField.value = String(r.courseGuests);
        markForecast();
      })
      .catch(() => {
        // ★欄は変えない。未保存の編集が残っているなら、その表示を消さない
        forecastStatus.textContent = `${dirtyForecast ? "編集中（未保存）\u3000" : ""}予約サイトから読み込めませんでした`;
      })
      .finally(() => {
        loadButton.disabled = false;
        saveForecast.disabled = false;
      });
  });

  const preview = el("pre");
  preview.className = "daily-report-preview";
  const copy = el("button", "報告文をコピー");
  copy.type = "button";
  const copyStatus = el("p");
  copyStatus.setAttribute("role", "status");

  /*
   * ★★**報告文の振り返りは「保存済みの本文」から作る**（引き継ぎ §3.5）。
   *   編集中の文字をそのまま載せると、画面に出ている報告文と保存されている日報が食い違う。
   *   ★食い違いを黙って起こさないために、未保存なら報告文にも「（未保存）」と出る。
   */
  let savedComment = input.saved;
  const renderPreview = (): void => {
    preview.textContent = buildDailyReportText({
      businessDate: input.businessDate,
      figures,
      comment: savedComment,
      footer: input.footer ?? null,
    });
  };
  renderPreview();

  area.addEventListener("input", () => {
    dirtyComment = area.value === (savedComment ?? "") ? null : area.value;
    status.textContent = dirtyComment === null ? "" : "編集中（未保存）";
  });

  save.addEventListener("click", () => {
    const value = area.value.trim();
    if (value === "") {
      status.textContent = "振り返りを入力してください。";
      return;
    }
    save.disabled = true;
    area.disabled = true;
    status.textContent = "保存中…";
    void input.api
      .save(value)
      .then((result) => {
        savedComment = result.comment;
        area.value = result.comment;
        dirtyComment = null;
        status.textContent = "✓ 保存しました";
        renderPreview();
      })
      .catch((error: unknown) => {
        // ★失敗しても入力は消さない（§4「保存状態」）
        status.textContent =
          error instanceof ApiError && error.status === 409
            ? "この月は締め処理が済んでいるため保存できません。"
            : "保存できませんでした。入力は残っています。もう一度お試しください。";
      })
      .finally(() => {
        save.disabled = false;
        area.disabled = false;
      });
  });

  section.append(el("h3", "LINE へ貼る報告文"), preview);
  // ★「—」の理由は文面に入れず、ここで添える（コピーした LINE の文を汚さない）
  const note = el("p");
  note.className = "daily-report-reasons muted";
  const renderReasons = (): void => {
    const reasons = reportFigureReasons(figures);
    note.textContent = `出せなかった項目（—）：${reasons.join(" ／ ")}`;
    note.hidden = reasons.length === 0;
  };
  renderReasons();
  section.append(note, copy, copyStatus);

  saveForecast.addEventListener("click", () => {
    const purchaseEstimate = forecastAmountToSend(purchaseField.value);
    const salesEstimate = forecastAmountToSend(salesField.value);
    const counts = countFields.map((f) => forecastAmountToSend(f.value));
    if (purchaseEstimate === undefined || salesEstimate === undefined) {
      forecastStatus.textContent = "金額は 0 以上の整数（円）で入力してください。空欄は未入力になります。";
      return;
    }
    const [reservationGroups, reservationGuests, courseGuests, shiftStaff] = counts;
    if (
      reservationGroups === undefined ||
      reservationGuests === undefined ||
      courseGuests === undefined ||
      shiftStaff === undefined
    ) {
      forecastStatus.textContent = "予約・シフトの人数は 0 以上の整数で入力してください。空欄は未入力になります。";
      return;
    }
    saveForecast.disabled = true;
    loadButton.disabled = true;
    purchaseField.disabled = true;
    salesField.disabled = true;
    for (const f of countFields) f.disabled = true;
    forecastStatus.textContent = "保存中…";
    void input.api
      .saveForecast({ purchaseEstimate, salesEstimate, reservationGroups, reservationGuests, courseGuests, shiftStaff })
      .then((next) => {
        // ★保存は成功している。取り直しに失敗したときも、入力は保存済みとして扱う
        savedPurchase = savedText(next === null ? purchaseEstimate : next.forecast.purchaseEstimate.value);
        savedSales = savedText(next === null ? salesEstimate : next.forecast.salesEstimate.value);
        purchaseField.value = savedPurchase;
        salesField.value = savedSales;
        savedCountTexts =
          next === null
            ? [reservationGroups, reservationGuests, courseGuests, shiftStaff].map(savedText)
            : savedCounts(next.forecast);
        countFields.forEach((f, i) => (f.value = savedCountTexts[i] ?? ""));
        dirtyForecast = false;
        if (next === null) {
          forecastStatus.textContent = "✓ 保存しました（報告文の更新に失敗しました。再読み込みしてください）";
          return;
        }
        figures = next;
        forecastStatus.textContent = "✓ 保存しました";
        renderPreview();
        renderReasons();
      })
      .catch((error: unknown) => {
        // ★失敗しても入力は消さない
        forecastStatus.textContent =
          error instanceof ApiError && error.status === 409
            ? "この月は締め処理が済んでいるため保存できません。"
            : error instanceof ApiError && error.status === 400
              ? error.message
              : "保存できませんでした。入力は残っています。もう一度お試しください。";
      })
      .finally(() => {
        saveForecast.disabled = false;
        loadButton.disabled = false;
        purchaseField.disabled = false;
        salesField.disabled = false;
        for (const f of countFields) f.disabled = false;
      });
  });

  copy.addEventListener("click", () => {
    const clipboard = input.clipboard ?? (typeof navigator === "undefined" ? undefined : navigator.clipboard);
    /*
     * ★**コピーできなかったら、手で選べるようにする**（引き継ぎ §3.5）。
     *   ★「コピーしました」とは書かない——書くと、貼り付けたつもりの空欄が送られる。
     * ★成功しても「送信済み」「報告済み」とは書かない。送るのは利用者。
     */
    const fallback = (): void => {
      copyStatus.textContent = "コピーできませんでした。本文を選択して手でコピーしてください。";
      const range = document.createRange();
      range.selectNodeContents(preview);
      const selection = window.getSelection();
      selection?.removeAllRanges();
      selection?.addRange(range);
    };
    if (clipboard === undefined) {
      fallback();
      return;
    }
    void clipboard
      .writeText(preview.textContent ?? "")
      .then(() => {
        copyStatus.textContent = "コピーしました。LINE に貼り付けて送信してください。";
      })
      .catch(fallback);
  });

  return section;
}
